import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { QcChartPoint, QcChartSummary } from '../../services/qcService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Title,
);

/**
 * RPD chart for duplicate (BIS) samples.
 * Each bar = |orig − bis| / mean × 100 (already computed and persisted as `value` by the engine).
 * A horizontal threshold is overlaid (default 10 % unless overridden by the chart's UCL).
 */

export interface RpdChartProps {
  summary: QcChartSummary | null;
  points: QcChartPoint[];
  height?: number;
  threshold?: number;
}

export const RpdChart: React.FC<RpdChartProps> = ({ summary, points, height = 280, threshold }) => {
  const data = useMemo(() => {
    const labels = points.map((p) =>
      new Date(p.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
    );
    const values = points.map((p) => Number(p.value));
    const limit = threshold ?? summary?.ucl ?? 10;

    const colors = points.map((p) =>
      p.out_of_control ? '#d32f2f' : p.warning_level ? '#ef6c00' : '#2e7d32',
    );

    return {
      labels,
      datasets: [
        {
          label: 'RPD (%)',
          type: 'bar' as const,
          data: values,
          backgroundColor: colors,
          borderColor: colors,
          borderWidth: 1,
        },
        {
          label: `Limite RPD (${limit} %)`,
          type: 'line' as const,
          data: labels.map(() => limit),
          borderColor: '#d32f2f',
          borderDash: [4, 4],
          pointRadius: 0,
          fill: false,
        },
      ],
    };
  }, [points, summary, threshold]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom' as const, labels: { boxWidth: 12 } },
        tooltip: { mode: 'index' as const, intersect: false },
      },
      scales: {
        y: { title: { display: true, text: 'RPD (%)' }, beginAtZero: true },
        x: { ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 12 } },
      },
    }),
    [],
  );

  return (
    <div style={{ height }}>
      <Bar data={data as any} options={options as any} />
    </div>
  );
};

export default RpdChart;
